import { useMemo, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Edges } from "@react-three/drei";
import { wallBoxes, roomColor, floorView, levelZ, stairBoxes } from "@/lib/walls";

function allBoxes(plan) {
  const n = plan.floors.length;
  return plan.floors.flatMap((f, i) => {
    const z = levelZ(plan, i);
    const walls = wallBoxes(floorView(plan, i)).map((b) => ({ ...b, z0: b.z0 + z, z1: b.z1 + z }));
    const slabs = i === 0 ? [] : f.rooms.filter((r) => r.type !== "stair")
      .map((r) => ({ x0: r.x, y0: r.y, x1: r.x + r.w, y1: r.y + r.h, z0: z - 1, z1: z, mat: "slab" }));
    const stairs = i < n - 1 ? f.rooms.filter((r) => r.type === "stair").flatMap((r) => stairBoxes(r, z, levelZ(plan, i + 1))) : [];
    const fit = (f.fitout?.items || []).map((it) => ({ x0: it.x, y0: it.y, x1: it.x + it.w, y1: it.y + it.h, z0: z, z1: z + (it.type === "counter" ? 0.1 : it.z), mat: it.cat }));
    return [...walls, ...slabs, ...stairs, ...fit];
  });
}

const Block = ({ b, cx, cz, wire }) => {
  const sx = b.x1 - b.x0, sy = b.z1 - b.z0, sz = b.y1 - b.y0;
  const glass = b.mat === "glass";
  const color = { slab: "#334155", stair: "#C084FC", furniture: "#A8957D", fixture: "#93C5FD", appliance: "#FFB800" }[b.mat] || "#E2E8F0";
  const edges = !["stair", "furniture", "fixture", "appliance"].includes(b.mat);
  return (
    <mesh position={[b.x0 + sx / 2 - cx, b.z0 + sy / 2, b.y0 + sz / 2 - cz]} castShadow receiveShadow>
      <boxGeometry args={[sx, sy, sz]} />
      {glass ? (
        <meshStandardMaterial color="#00F0FF" transparent opacity={0.25} metalness={0.2} roughness={0.1} />
      ) : (
        <meshStandardMaterial color={color} wireframe={wire} roughness={0.85} />
      )}
      {!wire && !glass && edges && <Edges color="#26334D" />}
    </mesh>
  );
};

const VIEWS = { iso: (m) => [m * 0.9, m * 0.8, m * 0.9], top: (m) => [0, m * 1.5, 0.01], front: (m) => [0, m * 0.3, m * 1.2] };

export const Massing3D = ({ plan }) => {
  const [wire, setWire] = useState(false);
  const [view, setView] = useState("iso");
  const boxes = useMemo(() => allBoxes(plan), [plan]);
  const cx = plan.width / 2, cz = plan.depth / 2;
  const m = Math.max(plan.width, plan.depth) + levelZ(plan, plan.floors.length) * (plan.floors.length > 1 ? 1.1 : 0.3);

  return (
    <div className="relative w-full h-full" data-testid="massing-3d">
      <Canvas key={view} shadows camera={{ position: VIEWS[view](m), fov: 40 }}>
        <color attach="background" args={["#0A0D14"]} />
        <ambientLight intensity={0.5} />
        <directionalLight position={[m, m * 1.4, m * 0.6]} intensity={1.4} castShadow shadow-mapSize={[2048, 2048]} />
        <gridHelper args={[m * 3, m * 3 / 2, "#26334D", "#121824"]} position={[0, -0.51, 0]} />
        <mesh position={[0, -0.25, 0]} receiveShadow>
          <boxGeometry args={[plan.width, 0.5, plan.depth]} />
          <meshStandardMaterial color="#182030" />
        </mesh>
        {plan.floors.flatMap((f, i) => f.rooms.map((r) => ({ ...r, z: levelZ(plan, i) }))).filter((r) => r.type !== "stair").map((r) => (
          <mesh key={r.id} position={[r.x + r.w / 2 - cx, r.z + 0.01, r.y + r.h / 2 - cz]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
            <planeGeometry args={[r.w, r.h]} />
            <meshStandardMaterial color={roomColor(r.type)} transparent opacity={0.22} />
          </mesh>
        ))}
        {boxes.map((b, i) => <Block key={i} b={b} cx={cx} cz={cz} wire={wire} />)}
        <OrbitControls makeDefault enableDamping />
      </Canvas>
      <div className="absolute top-4 left-4 flex gap-1 glass border border-[var(--line)] p-1 font-mono text-[11px] uppercase tracking-widest">
        {Object.keys(VIEWS).map((v) => (
          <button key={v} data-testid={`view-${v}-btn`} onClick={() => setView(v)}
            className={`px-3 py-1.5 transition-colors ${view === v ? "bg-[var(--cyan)] text-[var(--bg)]" : "text-[var(--muted)] hover:text-white"}`}>{v}</button>
        ))}
        <button data-testid="wireframe-toggle-btn" onClick={() => setWire((w) => !w)}
          className={`px-3 py-1.5 transition-colors ${wire ? "bg-[var(--amber)] text-[var(--bg)]" : "text-[var(--muted)] hover:text-white"}`}>wire</button>
      </div>
      <p className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">Drag to orbit · scroll to zoom · right-drag to pan</p>
    </div>
  );
};
